import { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { getBausteinPrices, type BausteinPrice } from "@/lib/villaApi";
import {
  currencySymbol,
  formatAmount,
  getEurToUsdRate,
  setEurToUsdRate,
  toUsd,
} from "@/lib/currency";

// Fallback-Standardwerte in EURO (Basis), damit die Seiten auch dann korrekt rechnen,
// wenn die Preise (noch) nicht geladen werden konnten. US$ wird daraus umgerechnet.
export const DEFAULT_PRICES: Record<string, number> = {
  villa_bougainvillea_1: 107,
  villa_frangipani_1: 107,
  villa_frangipani_2: 181,
  villa_hibiskus_2: 214,
  villa_hibiskus_3: 274,
  villa_gesamt: 563,
  day_trip: 98,
  day_trip_child: 59,
  rafting: 56,
  sailing: 130,
  massage: 28,
  dinner: 14,
  roundtrip_8_adult: 234,
  roundtrip_8_child: 146,
  roundtrip_9_adult: 390,
  roundtrip_9_child: 234,
  roundtrip_10_adult: 530,
  roundtrip_10_child: 318,
  roundtrip_11_adult: 586,
  roundtrip_11_child: 352,
  roundtrip_12_adult: 642,
  roundtrip_12_child: 385,
  transfer_1: 56,
  transfer_2: 112,
  transfer_3: 167,
  transfer_region_1: 84,
  transfer_region_2: 167,
  discount_percent: 5,
  discount_nights: 5,
  discount_excursions: 3,
  wedding_standesamt: 1637,
  wedding_ohne_standesamt: 1014,
  wedding_eheversprechen: 1014,
  wedding_jubilaeum: 1014,
  wedding_addon_photographer: 535,
  wedding_addon_photosession: 116,
  wedding_addon_candle_dinner: 64,
  wedding_addon_cake: 51,
  wedding_addon_hairdresser: 51,
  wedding_addon_honeymooner: 214,
  wedding_addon_extra_villa: 181,
};

// Einheiten, die keine Geldbeträge sind und daher nicht umgerechnet werden.
const NON_CURRENCY_UNITS = new Set(["percent", "nights", "excursions", "rate"]);
const NON_CURRENCY_KEYS = new Set(["discount_percent", "discount_nights", "discount_excursions"]);

// Schlüssel der Datenbank-Zeile, in der der gepflegte Umrechnungskurs (1 € → US$) liegt.
const RATE_KEY = "eur_to_usd_rate";

// Liest den Umrechnungskurs aus den geladenen Preiszeilen (Fallback: Standard).
const applyRateFromRows = (rows: BausteinPrice[]): void => {
  const row = rows.find((r) => r.key === RATE_KEY || r.unit === "rate");
  if (row) {
    setEurToUsdRate(Number(row.amount));
  }
};

// Gemeinsamer Abruf, damit parallele Hooks nur einmal laden. Der Zwischenspeicher
// gilt nur für gleichzeitige Anfragen und wird danach geleert, damit der Kurs
// nach einer Änderung im Backstage wieder frisch geladen wird.
let inFlight: Promise<BausteinPrice[] | null> | null = null;

const loadRows = (): Promise<BausteinPrice[] | null> => {
  if (inFlight) return inFlight;
  inFlight = getBausteinPrices()
    .then(({ data, error }) => {
      if (error || !data || data.length === 0) return null;
      applyRateFromRows(data);
      return data;
    })
    .catch(() => null)
    .finally(() => {
      inFlight = null;
    });
  return inFlight;
};

export function usePriceMap() {
  const { i18n } = useTranslation();
  const isDe = i18n.language.startsWith("de");
  const [rows, setRows] = useState<BausteinPrice[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    loadRows()
      .then((data) => {
        if (cancelled) return;
        if (data) setRows(data);
        setLoading(false);
      })
      .catch(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const priceMap = useMemo(() => {
    const map: Record<string, number> = {};

    if (rows.length === 0) {
      // Fallback auf die Standardwerte (Euro), sprachabhängig umgerechnet.
      Object.entries(DEFAULT_PRICES).forEach(([key, eur]) => {
        map[key] = !isDe && !NON_CURRENCY_KEYS.has(key) ? toUsd(eur) : eur;
      });
      return map;
    }

    rows.forEach((p) => {
      const isCurrency = !NON_CURRENCY_UNITS.has(p.unit);
      const eur = Number(p.amount);
      map[p.key] = !isDe && isCurrency ? toUsd(eur) : eur;
    });
    return map;
  }, [rows, isDe]);

  return {
    priceMap,
    currency: currencySymbol(isDe),
    money: (amount: number) => formatAmount(amount, isDe),
    isDe,
    loading,
  };
}

// Stellt den aktuellen Umrechnungskurs (1 € → US$) bereit und lädt ihn bei Bedarf
// aus der Datenbank. Nützlich für Seiten, die den Kurs direkt (über money/toUsd) nutzen.
export function useExchangeRate(): number {
  const [rate, setRate] = useState(getEurToUsdRate());

  useEffect(() => {
    let cancelled = false;
    loadRows().then(() => {
      if (!cancelled) setRate(getEurToUsdRate());
    });
    return () => {
      cancelled = true;
    };
  }, []);

  return rate;
}